// review-summary.ts — pure summarizer for review co-writer (Phase 2.2).
// Builds a compact ReviewSummary from LifeOsState for a given scope + today.
// No network, no side effects — fully unit-testable.
// DRY: delegates stat aggregation to review-derive helpers; only doneTitles computed here.

import {
  weekStats,
  monthStats,
  weekStartIso,
  currentMonthId,
} from "../../bento/review-derive";
import { addDaysIso } from "../date";
import type { LifeOsState } from "../../types";
import type { ReviewStats } from "../../bento/review-derive";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ReviewSummary {
  scope: "week" | "month";
  /** Human-readable label in Vietnamese: e.g. "tuần này (09–15/06)" or "tháng 6/2026". */
  periodLabel: string;
  /** Aggregated stats — reused from review-derive helpers (DRY). */
  stats: ReviewStats;
  /** Deduplicated list of done task titles in the period (capped at 8). */
  doneTitles: string[];
}

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Parse a local ISO date string to Date at local midnight. */
function fromLocal(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/** Zero-pad a number to 2 digits. */
function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Format an ISO date string to "dd/mm" (local, Vietnamese style).
 * e.g. "2026-06-09" → "09/06"
 */
function fmtDayMonth(iso: string): string {
  const d = fromLocal(iso);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

/**
 * Build a human-readable period label for the week containing `today`.
 * Format: "tuần này (07–13/06)" — day range of the Mon–Sun window.
 */
function buildWeekLabel(today: string): string {
  const wStart = weekStartIso(today);
  const wEnd = addDaysIso(6, fromLocal(wStart));
  return `tuần này (${fmtDayMonth(wStart)}–${fmtDayMonth(wEnd)})`;
}

/**
 * Build a human-readable period label for the calendar month of `today`.
 * Format: "tháng 6/2026"
 */
function buildMonthLabel(today: string): string {
  const monthId = currentMonthId(today); // "YYYY-MM"
  const [year, month] = monthId.split("-");
  return `tháng ${Number(month)}/${year}`;
}

/**
 * Collect deduplicated done-task titles from dailyReviews whose `date` is
 * within the period window.
 *
 * Week window  : [weekStart, weekStart + 6 days] inclusive ISO comparison.
 * Month window : date.slice(0,7) === monthId.
 *
 * Missing/deleted task ids are silently skipped (user may have deleted them).
 * Result is deduped and capped at 8 titles to keep the AI prompt compact.
 */
function collectDoneTitles(
  state: LifeOsState,
  inWindow: (date: string) => boolean,
): string[] {
  // Build a quick lookup map for tasks by id (O(n) once, O(1) per lookup).
  const taskMap = new Map<string, string>(); // id → title
  for (const t of state.tasks) {
    taskMap.set(t.id, t.title);
  }

  const seen = new Set<string>(); // dedup by title
  const titles: string[] = [];

  for (const rev of state.dailyReviews) {
    if (!inWindow(rev.date)) continue;
    for (const taskId of rev.doneTaskIds) {
      const title = taskMap.get(taskId);
      if (!title || seen.has(title)) continue;
      seen.add(title);
      titles.push(title);
      if (titles.length >= 8) return titles; // hard cap
    }
  }
  return titles;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build a compact ReviewSummary from state for the given scope and reference date.
 *
 * @param state  Full LifeOsState (read-only — never mutated).
 * @param scope  "week" | "month".
 * @param today  ISO date string YYYY-MM-DD (injected; no Date() calls inside).
 */
export function buildReviewSummary(
  state: LifeOsState,
  scope: "week" | "month",
  today: string,
): ReviewSummary {
  if (scope === "week") {
    const wStart = weekStartIso(today);
    const wEnd = addDaysIso(6, fromLocal(wStart));
    const stats = weekStats(state, wStart);
    const doneTitles = collectDoneTitles(
      state,
      (date) => date >= wStart && date <= wEnd,
    );
    return {
      scope,
      periodLabel: buildWeekLabel(today),
      stats,
      doneTitles,
    };
  }

  // scope === "month"
  const monthId = currentMonthId(today);
  const stats = monthStats(state, monthId);
  const doneTitles = collectDoneTitles(
    state,
    (date) => date.slice(0, 7) === monthId,
  );
  return {
    scope,
    periodLabel: buildMonthLabel(today),
    stats,
    doneTitles,
  };
}
